<template>
  <div class="soar-container mx-auto h-full pt-8">
    <ServiceFormNameSkeleton v-if="loading"></ServiceFormNameSkeleton>
    <template v-else>
      <div>
        <H1Typo class="pb-4 border-b border-brand-grayblue-300">
          {{
            globalStore.startedService?.name?.text?.serviceTransactionName.replaceAll(' - ', '-')
          }}
        </H1Typo>
        <div class="flex flex-wrap flex-col items-center md:flex-row gap-4 md:gap-12 pt-6">
          <div class="flex-1 flex flex-col gap-y-3">
            <h2 class="text-2xl pb-4 border-b border-brand-grayblue-300">
              {{ $t('serviceDesc') }}
            </h2>
            <p
              v-html="globalStore.startedService?.name?.serviceCard?.serviceDescription"
              class="text-justify"
            ></p>
          </div>
          <RadialProgress :percentage="radialPercentage" class="order-3 md:order-2" />
          <div class="order-2 md:order-3 w-full md:w-1/4 self-baseline flex justify-stretch">
            <RouterLink
              :to="{ name: 'ServiceCardView', params: { serviceId } }"
              class="w-full py-2 rounded border border-brand-gray-600 bg-brand-gray-300 shadow-md text-center"
            >
              {{ $t('serviceCard') }}
            </RouterLink>
          </div>
        </div>
      </div>
      <div
        class="flex flex-wrap flex-col items-center md:items-start md:flex-row gap-4 md:gap-12 pt-6"
      >
        <div class="w-full flex-1 flex flex-col gap-y-3">
          <FormWrapper>
            <template #header>
              <StepperVue
                :steps="formStore.schema?.stepLabels?.web"
                v-model="activeStepIndex"
                disabled
              ></StepperVue>
            </template>
            <component
              :is="activeStepComponent"
              :key="formStore.activeStep"
              @on-back="handleBack"
              @on-next="handleNext"
            ></component>
          </FormWrapper>
        </div>
        <div class="order-2 md:order-3 w-full md:w-1/4 self-baseline"></div>
      </div>
    </template>
  </div>
</template>

<script setup>
import FormWrapper from '@/components/Form/FormWrapper.vue'
import RadialProgress from '@/components/Ui/RadialProgress.vue'
import ServiceFormNameSkeleton from '@/components/Ui/Skeletons/ServiceForm/name.vue'
import StepperVue from '@/components/Ui/Stepper.vue'
import H1Typo from '@/components/typography/h1.vue'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { useLookupsStore } from '@/stores/lookups'
import FeesStep from '@/views/service-form/FeesStep/index.vue'
import AttachmentsStep from '@/views/service-form/_AttachmentsStep.vue'
import RequestStep from '@/views/service-form/_RequestStep.vue'
import ReviewStep from '@/views/service-form/_ReviewStep.vue'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute } from 'vue-router'

const globalStore = useGlobalStore()
const formStore = useFormStore()
const i18n = useI18n()
const route = useRoute()
const activeStepIndex = ref(0)

const stepComponents = {
  workPermitNumber: RequestStep,
  tripDetail: RequestStep,
  groupDrafts: RequestStep,
  applicantInformation: RequestStep,
  personalInformation: RequestStep,
  securityDeposit: RequestStep,
  insurancePolicy: RequestStep,
  DeliveryAddressInformation: RequestStep,
  bookAnAppointment: RequestStep,
  requestInformation: RequestStep,
  requestDetails: RequestStep,
  PersonalInfo: RequestStep,
  applicant: RequestStep,
  addresses: RequestStep,
  sponsoredInfo: RequestStep,
  sponseredInfo: RequestStep,
  SponseredInfo: RequestStep,
  entryExitTransactions: RequestStep,
  joinCrewToTripRequest: RequestStep,
  attachments: AttachmentsStep,
  review: ReviewStep,
  ApplicationFees: FeesStep
}

globalStore.languageId = i18n.locale.value == 'en' ? 2 : 1

const serviceId = +route.params.serviceId
const loading = ref(true)

const resetForm = async () => {
  loading.value = true
  await formStore.resetForm(serviceId)

  loading.value = false
}

onMounted(async () => {
  await resetForm(serviceId, false)
})

const activeStepComponent = computed(() => {
  // return something else when step isn't declared
  if (!Object.keys(stepComponents).includes(formStore.activeStep)) {
    return ''
  }

  return stepComponents[formStore.activeStep]
})

const radialPercentage = computed(() => {
  const numberOfSteps = formStore.schema?.stepLabels.web.length

  const currentStep = formStore.activeStepIndex
  if (currentStep === 0) return 0

  return ((currentStep || 1) / numberOfSteps) * 100
})

const handleBack = () => {
  formStore.activeStep = formStore.schema.stepLabels.web[formStore.activeStepIndex - 1]
}

const handleNext = () => {
  formStore.activeStep = formStore.schema.stepLabels.web[formStore.activeStepIndex + 1]
}

onBeforeRouteLeave(() => {
  loading.value = true
  formStore.$reset()
  useLookupsStore().$reset()
  sessionStorage.clear()
  globalStore.$patch((state) => {
    state.startedService = null
  })
})
</script>

<script>
export default { name: 'ServiceForm' }
</script>

<style lang="scss">
.jsoneditor-vue {
  height: 100%;
}
</style>
